import { type AggType } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type EventStandardDefs } from '@solinkcorp/event-standard';
import { type AggregationGroup, type AggregationResult } from 'components/DiscoverPage/types';
import { scaleLinear } from 'd3-scale';
import * as EventUtil from './EventUtil';

export function getNumber(d: AggregationResult, aggType: AggType) {
  let number = d.DocCount;
  if (!d.Aggregations) {
    return number;
  }

  if (!aggType && d.Aggregations.subStatsAgg.Meta) {
    aggType = d.Aggregations.subStatsAgg.Meta.aggType;
  }

  if (aggType === 'Average') {
    number = d.Aggregations.subStatsAgg.Average || 0;
  }

  if (aggType === 'Sum') {
    number = d.Aggregations.subStatsAgg.Sum || 0;
  }

  return number;
}

export function getAggType(d: AggregationResult) {
  let aggType: AggType = 'Count';

  if (d.Aggregations && d.Aggregations.subStatsAgg.Meta) {
    aggType = d.Aggregations.subStatsAgg.Meta.aggType;
  }

  return aggType;
}

export function getAverage(dataGroup: AggregationGroup, aggType: AggType) {
  let sum = 0;
  let count = 0;

  Object.keys(dataGroup).forEach((key) => {
    const d = dataGroup[key];

    if (aggType === 'Count') {
      sum += d.DocCount;
    } else {
      if (!d.Aggregations) {
        d.Aggregations = { subStatsAgg: { Meta: { aggType } } };
      }
      sum += d.Aggregations.subStatsAgg[aggType] || 0;
    }

    count++;
  });

  return sum / count;
}

export function prettifyTerm(
  eventStandard: EventStandardDefs,
  field: string | undefined,
  localeCode?: string,
): string | undefined {
  if (!field) {
    return;
  }

  if (field === 'locationId' || field === 'siteId') {
    field = 'location';
  }

  if (EventUtil.i18nEventFieldData(field, eventStandard, localeCode)) {
    return EventUtil.i18nEventFieldName(field, true, eventStandard, localeCode);
  }

  if (!/\./.test(field)) {
    return field;
  }

  return field.split(/\./)[1];
}

export function determineGroups(dataset: number[]) {
  const maxY = Math.max(...dataset.map(Math.ceil));
  const minY = Math.min(...dataset.map(Math.floor));

  const height = 260;
  const groupSize = 16;

  const yScale = scaleLinear().domain([minY, maxY]).range([height, 0]);

  const groups: { [group: string]: { [group: string]: AggregationResult } } = {};
  for (let i = 0; i < height; i += groupSize) {
    const yValue = yScale.invert(i);
    let group: string;
    if (maxY < 1) {
      group = yValue.toFixed(2);
    } else {
      group = yValue.toFixed(0);
    }

    groups[group] = {};
  }

  groups[minY] = {};
  return groups;
}

export function findMyGroup(dataset: { [key: number]: unknown }, num: number): number {
  const debug = false;
  const keys = Object.keys(dataset);
  const maxY = parseInt(keys[keys.length - 1], 10);
  const arr = Object.keys(dataset)
    .map((d) => {
      return maxY > 1 ? parseInt(d) : parseFloat(parseFloat(d).toFixed(2));
    })
    .sort((a, b) => {
      return a - b;
    });

  if (debug) {
    console.debug(`findMyGroup: ${num} in ${arr}。。。`);
  }

  let l = 0;
  let r = arr.length - 1;

  while (r - l > 1) {
    const m = (r + l) >> 1;
    if (debug) {
      console.debug(`m: ${arr[m]}, l: ${arr[l]}, r: ${arr[r]}`);
    }

    if (arr[m] > num) {
      r = m;
    } else {
      l = m;
    }
  }

  if (num === arr[r]) {
    if (debug) {
      console.debug(`R ==> ${arr[r]}`);
    }

    return arr[r];
  }

  if (debug) {
    console.debug(`L ==> ${arr[l]}`);
  }

  return arr[l];
}
